<template>
  <b-modal
    ref="modal-reise-edit"
    auto-focus-button="ok"
    id="modal-reise-edit"
    content-class="modal-form"
    :title="isAddingReise ? $t('REISEN.MODALS.ADDTITLE') : $t('REISEN.MODALS.EDITTITLE')"
    centered
    @hidden="onHiddenModal"
    @ok="handleOk"
  >
    <b-form ref="form" @submit.stop.prevent="handleSubmit">
      <b-form-group label-for="reisekuerzel" :invalid-feedback="validateState('reisekuerzel', true)">
        <slot name="label">
          <label>
            {{ $t('REISEN.COMMON.REISEKUERZEL') }}
            <span v-b-popover.hover.top="$t('REISEN.POPOVERTEXT.Widget1')">
              <CircleHelp :size="16" class="ml-1" />
            </span>
          </label>
        </slot>
        <b-form-input
          id="reisekuerzel"
          v-model="$v.form.reisekuerzel.$model"
          :formatter="v => v.toUpperCase()"
          :state="validateState('reisekuerzel')"
          type="text"
          trim
          :disabled="!isAddingReise"
        ></b-form-input>
      </b-form-group>
      <b-form-group label="Reisetitel" label-for="titel" :invalid-feedback="validateState('titel', true)">
        <b-form-input
          id="titel"
          type="text"
          v-model="$v.form.titel.$model"
          :state="validateState('titel')"
        ></b-form-input>
      </b-form-group>
      <b-form-group
        :invalid-feedback="validateState('country', true)"
        :state="validateState('country')"
        :label="$t('REISEN.LANDEINGEBEN')"
        label-for="country"
      >
        <AppSelect
          id="country"
          v-model="$v.form.country.$model"
          :options="countriesOptions"
          label="Land"
          placeholder="Land wählen"
          :state="validateState('country')"
        />
      </b-form-group>
      <b-form-group label="Weitere Länder" label-for="weitereLaender">
        <AppSelect
          id="weitereLaender"
          v-model="form.weitereLaender"
          :options="countriesOptions"
          clearable
          label="Weitere Länder"
          placeholder="Länder auswählen"
          multiple
        />
      </b-form-group>

      <b-form-group
        :invalid-feedback="validateState('region', true)"
        :state="validateState('region')"
        :label="$t('REISEN.COMMON.REGION')"
        label-for="region"
      >
        <AppSelect
          id="region"
          v-model="$v.form.region.$model"
          :options="regionOptions"
          label="Region"
          placeholder="Region wählen"
          :state="validateState('region')"
        />
      </b-form-group>

      <b-form-group
        :invalid-feedback="validateState('prioritaet', true)"
        :state="validateState('prioritaet')"
        :label="$t('REISEN.PRIORITAETEINGEBEN')"
        label-for="prioritaet"
      >
        <AppSelect
          id="prioritaet"
          v-model="$v.form.prioritaet.$model"
          sort="none"
          :options="prioritaetSelectOptions"
          label="Priorität"
          placeholder="Priorität wählen"
          hide-search
          :state="validateState('prioritaet')"
        />
      </b-form-group>
      <b-form-group
        :invalid-feedback="validateState('regionsmanager', true)"
        :state="validateState('regionsmanager')"
        :label="'Regionsmanager'"
        label-for="regionsmanager"
      >
        <AppSelect
          id="regionsmanager"
          v-model="$v.form.regionsmanager.$model"
          :options="regionsmanagerOptions"
          label="Regionsmanager"
          placeholder="Regionsmanager wählen"
          hide-search
          :state="validateState('regionsmanager')"
        />
      </b-form-group>

      <b-form-group label-for="einreisebestimmungenLink">
        <slot name="label">
          <label>
            Einreisebestimmungen (Link)
            <span v-b-popover.hover.top="$t('REISEN.POPOVERTEXT.Einreisebestimmungen')">
              <CircleHelp :size="16" class="ml-1" />
            </span>
          </label>
        </slot>
        <b-form-input
          id="einreisebestimmungenLink"
          v-model="form.einreisebestimmungenLink"
          placeholder="https://www.skr.de/?type=1568711051&destinations=NA"
        ></b-form-input>
      </b-form-group>

      <b-form-group style="margin-bottom: 16px">
        <b-form-checkbox v-model="form.isKritischeDestination" style="display: flex; align-items: center">
          Kritische Destination
        </b-form-checkbox>
      </b-form-group>

      <b-form-group style="margin-bottom: 16px">
        <b-form-checkbox
          switch
          id="automatischeAufgabeMtzErreicht"
          v-model="form.automatischeAufgabeMtzErreicht"
        >
          Automatische Aufgabe wird bei erreichter MTZ erstellt
        </b-form-checkbox>
      </b-form-group>
      <b-form-group style="margin-bottom: 16px">
        <b-form-checkbox switch id="automatischeAufgabeTktl" v-model="form.automatischeAufgabeTktl">
          Automatische Aufgabe “Kurzes TKTL freischalten/deaktivieren” wird erstellt
        </b-form-checkbox>
      </b-form-group>

      <b-form-group style="margin-bottom: 16px">
        <b-form-checkbox
          id="automatischeAufgabeFahrzeugpruefung"
          switch
          v-model="form.automatischeAufgabeFahrzeugpruefung"
        >
          Automatische Aufgabe "Terminspezifische Fahrzeugprüfung" wird erstellt
        </b-form-checkbox>
      </b-form-group>
    </b-form>
    <LoadSpinnerWidget v-if="isLoading" :dataAvailable="true" :isLoading="isLoading"></LoadSpinnerWidget>

    <template #modal-footer="{ ok, cancel }">
      <b-button class="dialog-btn-secondary" @click="cancel()">Abbrechen</b-button>
      <b-button
        class="dialog-btn"
        variant="primary"
        :disabled="disableErstellenButton"
        @click="handleSubmit()"
      >
        Erstellen
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import { mapState } from 'vuex';
import { UPDATE_REISE, SET_IS_ADDING_REISE, ADD_REISE } from '@/core/produkte/reisen/stores/reisen.module.js';
import { validationMixin } from 'vuelidate';
import { maxLength, minLength, required } from 'vuelidate/lib/validators';
import LoadSpinnerWidget from '@/components/common/load-spinner-widget.vue';

import { landOptions } from '@/core/common/iso3166Countries.js';
import { parseFullUsername } from '@/core/common/helpers/utils.js';
import configRegions from '@/core/produkte/reisen/regions.config.json';
import configPirority from '@/core/produkte/reisen/priority.config.json';
import AppSelect from '@/components/common/app-select/app-select.vue';
import { CircleHelp } from 'lucide-vue';

export default {
  mixins: [validationMixin],
  name: 'ReisenEditComponent',
  props: {
    reise: Object,
  },
  components: { LoadSpinnerWidget, AppSelect, CircleHelp },
  data() {
    return {
      isLoading: false,
      form: {
        id: null,
        reisekuerzel: null,
        country: null,
        weitereLaender: [],
        region: null,
        prioritaet: null,
        titel: null,
        regionsmanager: null,
        einreisebestimmungenLink: null,
        isKritischeDestination: null,
        automatischeAufgabeMtzErreicht: false,
        automatischeAufgabeTktl: true,
        automatischeAufgabeFahrzeugpruefung: false,
      },
      error: {},
    };
  },
  watch: {
    editingReise(reiseObject) {
      if (reiseObject) {
        this.initModal(reiseObject);
        this.$bvModal.show('modal-reise-edit');
      }
    },
    isAddingReise(isAddingReise) {
      if (isAddingReise) {
        this.initModal();
        this.$bvModal.show('modal-reise-edit');
      }
    },
  },
  computed: {
    ...mapState({ users: state => state.users['users'] }),
    disableErstellenButton() {
      return this.$v.form.$invalid || this.isLoading;
    },
    regionsmanagerOptions() {
      const allowedNames = ['Sylvia Kehr', 'Stefanie Geyer'];
      const fullName = user => `${user.givenName} ${user.surname}`;

      return this.users
        .filter(user => user.department === 'Yield Fulfillment' && allowedNames.includes(fullName(user)))
        .map(user => ({
          value: user.id,
          text: parseFullUsername(user),
        }));
    },
    regionOptions() {
      return Object.entries(configRegions)
        .reduce((acc, cur) => {
          acc.push({ value: cur[1].value, text: this.$t('REISEN.REGIONS.' + cur[1].value) });
          return acc;
        }, [])
        .sort((a, b) => a.text.localeCompare(b.text));
    },
    countriesOptions() {
      return landOptions();
    },
    prioritaetOptions() {
      return configPirority || [];
    },
    // Die Konfiguration ist ein Objekt, das Select erwartet eine Liste aus { value, text }
    prioritaetSelectOptions() {
      return Object.values(this.prioritaetOptions).map(prio => ({ value: prio.value, text: prio.value }));
    },
    ...mapState({
      editingReise: state => state.reisen.editingReise,
      isAddingReise: state => state.reisen.isAddingReise,
    }),
  },
  validations: {
    form: {
      reisekuerzel: {
        required,
        minLength: minLength(6),
        maxLength: maxLength(6),
      },
      country: {
        required,
      },
      region: {
        required,
      },

      prioritaet: {
        required,
      },
      titel: { required, minLength: minLength(1) },
      regionsmanager: { required },
    },
  },
  methods: {
    onHiddenModal() {
      if (!this.editingReise) {
        this.$store.commit(SET_IS_ADDING_REISE, false);
      }
    },
    initModal(reise = null) {
      this.form = {
        id: reise?.id || '',
        reisekuerzel: reise?.reisekuerzel || '',
        country: reise?.country || '',
        weitereLaender: reise?.weitereLaender || [],
        region: (reise && reise.region && this.$t('REISEN.REGIONS.' + reise?.region)) || '',
        prioritaet: this.prioritaetOptions[reise?.prioritaet]?.value || '',
        titel: reise?.titel || null,
        regionsmanager: reise?.regionsmanager || '',
        einreisebestimmungenLink: reise?.einreisebestimmungenLink || null,
        isKritischeDestination: reise?.isKritischeDestination || false,
        automatischeAufgabeMtzErreicht: reise?.automatischeAufgabeMtzErreicht || false,
        automatischeAufgabeTktl: reise?.automatischeAufgabeTktl || true,
        automatischeAufgabeFahrzeugpruefung: reise?.automatischeAufgabeFahrzeugpruefung || false,
      };
    },
    validateState(name, mode = false) {
      if (!mode) {
        const { $dirty, $error } = this.$v.form[name];
        return $dirty ? !$error : null;
      }
      if (this.$v.form[name].required != null && !this.$v.form[name].required) {
        return this.$t('FORMS.REQUIREDFIELD');
      }

      if (this.$v.form[name].alpha != null && !this.$v.form[name].alpha) {
        return this.$t('FORMS.BUCHSTABEN');
      }

      if (this.$v.form[name].minLength != null && !this.$v.form[name].minLength) {
        return `Mindestens ${this.$v.form[name].$params.minLength.min} Großbuchstaben erforderlich`;
      }

      if (this.$v.form[name].maxLength != null && !this.$v.form[name].maxLength) {
        return `Maximal ${this.$v.form[name].$params.maxLength.max} Großbuchstaben erlaubt`;
      }

      if (this.$v.form[name].between != null && !this.$v.form[name].between) {
        return this.$t('FORMS.MAXIMUMCHARACTERS', {
          min: this.$v.form[name].$params.between.min,
          max: this.$v.form[name].$params.between.max,
        });
      }
    },
    resetModal() {
      this.form = {
        reisekuerzel: null,
        country: null,
        weitereLaender: [],
        region: null,
        prioritaet: null,
        titel: null,
        regionsmanager: null,
        einreisebestimmungenLink: null,
        isKritischeDestination: false,
        automatischeAufgabeMtzErreicht: false,
        automatischeAufgabeTktl: true,
        automatischeAufgabeFahrzeugpruefung: false,
      };

      this.$nextTick(() => {
        this.$v.$reset();
      });
    },
    handleOk(bvModalEvt) {
      // Prevent modal from closing
      bvModalEvt.preventDefault();
      // Trigger submit handler
      this.handleSubmit();
    },
    handleSubmit() {
      this.$v.form.$touch();
      if (this.$v.form.$anyError) {
        return;
      }

      const savedForm = {
        ...this.form,
        prioritaet: Object.entries(this.prioritaetOptions).find(
          priority => priority[1].value === this.form.prioritaet
        )[0],
      };

      this.isLoading = true;
      const reiseStoreAction = !this.editingReise && this.isAddingReise ? ADD_REISE : UPDATE_REISE;
      this.$store
        .dispatch(reiseStoreAction, { reise: savedForm })
        .then(response => {
          this.$bvModal.hide('modal-reise-edit');

          this.toast(this.$t('WORKFLOWS.COMMON.SAVED'));
        })
        .catch(error => {
          console.error(error);
          this.toast('Fehler beim Speichern!');
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
  },
};
</script>
